import { useCallback, useEffect, useState } from 'react';
import { Alert, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useConfirm } from '../../components/dialogs/DialogProvider';

/**
 * Value emitted by / fed into `PeriodSelector` — mirrors the `period` /
 * `from` / `to` query params `useRevenuePortfolio` et al accept directly,
 * so a page can spread it straight into the hook call.
 */
export type PeriodValue = { token: string; from?: string; to?: string };

const PRESETS: { token: string; label: string }[] = [
  { token: 'today', label: 'Today' },
  { token: 'yesterday', label: 'Yesterday' },
  { token: '7d', label: '7d' },
  { token: '30d', label: '30d' },
  { token: '90d', label: '90d' },
  { token: 'mtd', label: 'MTD' },
  { token: 'custom', label: 'Custom' },
];

// Spec §8 — the custom range is deliberately uncapped. Above this span we
// don't hard-reject; we just warn (a non-blocking confirm) that it may
// take a few seconds, and proceed if the user says yes.
const LARGE_RANGE_DAYS = 400;

/** ISO YYYY-MM-DD for `d` days ago (local) — mirrors MachineDailyMetersTab. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Chip-row period picker (spec §8): Today · Yesterday · 7d · 30d · 90d ·
 * MTD · Custom, via a MUI `ToggleButtonGroup`. Selecting Custom reveals
 * From/To date pickers (the `MachineDailyMetersTab` two-`TextField`
 * pattern). `from > to` shows an inline error and never emits. A custom
 * span over 400 days doesn't hard-reject — it asks a non-blocking confirm
 * first and proceeds only if the user continues.
 *
 * Controlled component: `selected`/`customFrom`/`customTo` are a local
 * echo of `value`, kept in sync via effect so the picker also tracks an
 * external reset (e.g. a parent "reset period" action) without every
 * keystroke needing to round-trip through the parent first.
 */
export function PeriodSelector({ value, onChange }: { value: PeriodValue; onChange: (v: PeriodValue) => void }) {
  const confirm = useConfirm();
  const [selected, setSelected] = useState(value.token);
  const [customFrom, setCustomFrom] = useState(value.from ?? isoDaysAgo(30));
  const [customTo, setCustomTo] = useState(value.to ?? isoDaysAgo(0));

  useEffect(() => {
    setSelected(value.token);
    if (value.from) setCustomFrom(value.from);
    if (value.to) setCustomTo(value.to);
  }, [value.token, value.from, value.to]);

  const isCustom = selected === 'custom';
  const rangeError = isCustom && customFrom > customTo ? 'From date must be on or before To date.' : null;

  const emitCustom = useCallback(async (from: string, to: string) => {
    if (!from || !to || from > to) return; // inline error covers this — never emit
    const span = daysBetween(from, to);
    if (span > LARGE_RANGE_DAYS) {
      const proceed = await confirm({
        title: 'Large range',
        message: `This range covers ${span} days and may take a few seconds. Continue?`,
        confirmLabel: 'Continue',
      });
      if (!proceed) return;
    }
    onChange({ token: 'custom', from, to });
  }, [confirm, onChange]);

  const handlePresetChange = (_event: unknown, token: string | null) => {
    if (!token) return; // exclusive group re-click of the active button
    setSelected(token);
    if (token === 'custom') {
      void emitCustom(customFrom, customTo);
    } else {
      onChange({ token });
    }
  };

  const handleFromChange = (v: string) => {
    setCustomFrom(v);
    void emitCustom(v, customTo);
  };
  const handleToChange = (v: string) => {
    setCustomTo(v);
    void emitCustom(customFrom, v);
  };

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup exclusive size="small" value={selected} onChange={handlePresetChange}>
        {PRESETS.map((p) => (
          <ToggleButton key={p.token} value={p.token}>{p.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {isCustom && (
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="From" type="date" size="small" value={customFrom}
            onChange={(e) => handleFromChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!!rangeError} inputProps={{ 'aria-label': 'from date' }}
          />
          <TextField
            label="To" type="date" size="small" value={customTo}
            onChange={(e) => handleToChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!!rangeError} inputProps={{ 'aria-label': 'to date' }}
          />
        </Stack>
      )}
      {rangeError && <Alert severity="warning">{rangeError}</Alert>}
    </Stack>
  );
}
